import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Banknote, TrendingUp, Coins, Gift, ShieldAlert, Crown, Loader2,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";

const MONTH = "2026-06";

export default function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/admin/analytics?month=${MONTH}`).then((r) => setData(r.data));
  }, []);

  if (!data)
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  const t = data.totals;
  const cr = data.credit_risk;

  return (
    <div>
      <PageHeader
        title="Company Overview"
        subtitle={`Consolidated in AED · ${MONTH}`}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Sales" value={money(t.total_sales)} icon={Banknote} />
        <StatCard label="Total Profit" value={money(t.total_profit)} icon={TrendingUp} />
        <StatCard
          label="Commissions Paid"
          value={money(t.total_commission)}
          sub={`${t.commission_pct_of_sales}% of sales`}
          icon={Coins}
          accent
        />
        <StatCard label="Bonuses" value={money(t.total_bonuses)} icon={Gift} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold mb-4">Leaderboard · collected sales</h3>
          <div className="space-y-3">
            {data.leaderboard.map((a, i) => (
              <div key={a.agent_id} className="flex items-center gap-3" data-testid={`leaderboard-row-${i}`}>
                <span className="w-6 text-center font-display font-bold text-muted-foreground">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{a.name}</span>
                    {a.is_star && (
                      <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 gap-1">
                        <Crown className="w-3 h-3" /> Star
                      </Badge>
                    )}
                  </div>
                  <div className="h-1.5 bg-secondary rounded-full mt-1.5 overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{
                        width: `${Math.min(
                          100,
                          (a.collected_sales / data.leaderboard[0].collected_sales) * 100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
                <span className="font-display font-semibold text-sm">
                  {money(a.collected_sales)}
                </span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-1 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-500" /> Credit risk
          </h3>
          <p className="text-xs text-muted-foreground mb-4">Stock value out on credit vs deposit</p>
          <div className="space-y-3 text-sm">
            <Row label="Credit outstanding" value={money(cr.total_credit_out)} />
            <Row label="Deposits held" value={money(cr.total_deposit_held)} />
            <Row
              label="Deposit coverage"
              value={`${cr.coverage_pct}%`}
              danger={cr.coverage_pct < 100}
            />
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Near / over limit</p>
              {cr.near_or_over_limit.length === 0 ? (
                <p className="text-xs text-muted-foreground">All agents within safe limits.</p>
              ) : (
                cr.near_or_over_limit.map((a) => (
                  <div key={a.agent_id} className="flex justify-between text-xs py-1">
                    <span>{a.name}</span>
                    <Badge variant="destructive">
                      {money(a.outstanding_credit)} / {money(a.credit_limit)}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5 mt-4">
        <h3 className="font-display font-semibold mb-4">Sales by product (AED)</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.product_mix} margin={{ left: 0, right: 10 }}>
              <XAxis dataKey="product" tick={{ fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))" }}
                formatter={(v) => money(v)}
              />
              <Bar dataKey="sales" radius={[6, 6, 0, 0]}>
                {data.product_mix.map((_, i) => (
                  <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, danger }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold ${danger ? "text-destructive" : ""}`}>{value}</span>
    </div>
  );
}
